/**
 * Row alignment for the Compare PDF page. Pure functions over the rows getQuoteCompare and
 * getQuoteCompareDetail return, so the print page (which runs in a fresh browser with no
 * quote page state) can line the compared products up the way the Compare modal does.
 * Columns are product ids; a product with no row in a line gets null.
 */

export interface CompareRow {
  key: string;
  entity_id: number;
  label: string;
  img_path: string | null;
  cells: any[];
}

export interface CompareDetailGroup {
  group_id: number;
  group_name: string;
  rows: { key: string; label: string; description: string | null; img_path: string | null; cells: any[]; otherCells: any[] }[];
}

export interface CompareItemGroup {
  group_id: number;
  group_name: string;
  totalCells: any[];
  rows: { key: string; title: string; subtitle: string; cells: any[]; more: (string | null)[] }[];
}

/** Features / Included / Not included (getQuoteCompare): rows matched across the products by entity type and id. */
export function alignTopRows(rows: any[], entryType: string, productIds: number[]): CompareRow[] {
  const keyOf = (row: any) => `${row.entity_type ?? ''}:${row.entity_id}`;
  const own = rows.filter(row => row.entry_type === entryType);
  const order: string[] = [];
  const first = new Map<string, any>();
  for (const id of productIds) {
    for (const row of own.filter(r => r.product_id === id)) {
      if (!first.has(keyOf(row))) {
        first.set(keyOf(row), row);
        order.push(keyOf(row));
      }
    }
  }
  return order.map(key => {
    const cells = productIds.map(id => own.find(r => r.product_id === id && keyOf(r) === key) ?? null);
    const head = first.get(key);
    return { key, entity_id: head.entity_id, label: head.label, img_path: cells.find(cell => cell)?.img_path ?? null, cells };
  });
}

/** What's Covered / Not Covered / The Benefits (getQuoteCompareDetail): grouped by class, rows matched by statement within a class. */
export function alignDetailGroups(detail: any[], entryType: string, productIds: number[]): CompareDetailGroup[] {
  const keyOf = (row: any) => `${row.group_id}|${row.entity_key}`;
  const lookups = (type: string | null) => productIds.map(id => {
    const lookup = new Map<string, any>();
    for (const row of type ? detail.filter(r => r.product_id === id && r.entry_type === type) : []) {
      if (!lookup.has(keyOf(row))) lookup.set(keyOf(row), row);
    }
    return lookup;
  });
  const byProduct = lookups(entryType);
  const otherType = entryType === 'covered' ? 'not_covered' : entryType === 'not_covered' ? 'covered' : null;
  const otherByProduct = lookups(otherType);
  const groups = new Map<number, { group_name: string; order: string[]; first: Map<string, any> }>();
  for (const lookup of byProduct) {
    lookup.forEach((row, key) => {
      const group = groups.get(row.group_id) ?? { group_name: row.group_name, order: [], first: new Map<string, any>() };
      if (!group.first.has(key)) {
        group.first.set(key, row);
        group.order.push(key);
      }
      groups.set(row.group_id, group);
    });
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([group_id, group]) => ({
      group_id,
      group_name: group.group_name,
      rows: group.order.map(key => ({
        key,
        label: group.first.get(key).label,
        description: group.first.get(key).description ?? null,
        img_path: group.first.get(key).img_path ?? null,
        cells: byProduct.map(lookup => lookup.get(key) ?? null),
        otherCells: otherByProduct.map(lookup => lookup.get(key) ?? null)
      }))
    }));
}

/** Your Insured Items: a class header with each product's total, then the items; the dearer product's item gets a "more" tag. */
export function alignItemGroups(detail: any[], productIds: number[], frequency: string): CompareItemGroup[] {
  const rowsOf = (id: number) => detail.filter(r => r.product_id === id);
  const groups = new Map<number, { group_name: string; order: string[]; label: Map<string, string> }>();
  for (const id of productIds) {
    for (const row of rowsOf(id).filter(r => r.entry_type === 'item')) {
      const group = groups.get(row.group_id) ?? { group_name: row.group_name, order: [], label: new Map<string, string>() };
      if (!group.label.has(row.entity_key)) {
        group.label.set(row.entity_key, row.label ?? '');
        group.order.push(row.entity_key);
      }
      groups.set(row.group_id, group);
    }
  }
  const moreLabels = (cells: any[]): (string | null)[] => {
    const amounts = cells.filter(cell => cell?.amount != null).map(cell => cell.amount as number);
    if (amounts.length < 2) return cells.map(() => null);
    const lowest = Math.min(...amounts);
    return cells.map(cell => cell?.amount != null && cell.amount > lowest
      ? `R ${Math.round(cell.amount - lowest).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} /${frequency} more`
      : null);
  };
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([group_id, group]) => ({
      group_id,
      group_name: group.group_name,
      totalCells: productIds.map(id => rowsOf(id).find(r => r.entry_type === 'class_total' && r.group_id === group_id) ?? null),
      rows: group.order.map(key => {
        const cells = productIds.map(id => rowsOf(id).find(r => r.entry_type === 'item' && r.group_id === group_id && r.entity_key === key) ?? null);
        const parts = (group.label.get(key) ?? '').split('|');
        return { key, title: parts[0] ?? '', subtitle: parts[1] ?? '', cells, more: moreLabels(cells) };
      })
    }));
}

/** The Insurers: one row per rating type across the products, each cell the product's rating row. */
export function insurerRatingRows(detail: any[], productIds: number[]): { rating_type: string; cells: any[] }[] {
  const types: string[] = [];
  for (const id of productIds) {
    for (const row of detail.filter(r => r.product_id === id && r.entry_type === 'insurer_rating')) {
      if (!types.includes(row.label)) types.push(row.label);
    }
  }
  return types.map(rating_type => ({
    rating_type,
    cells: productIds.map(id => detail.find(r => r.product_id === id && r.entry_type === 'insurer_rating' && r.label === rating_type) ?? null)
  }));
}

/** The overall rating as the percent-per-star list the star markup takes, from the star string on any rating row. */
export function starPercents(detail: any[], productId: number): string[] {
  const row = detail.find(r => r.product_id === productId && r.entry_type === 'insurer_rating');
  return (row?.description ?? '').split('|').filter((p: string) => p !== '');
}

/** The overall rating out of 5 (each star percent counts as a fraction), as the card computes it. */
export function overallRating(detail: any[], productId: number): number {
  return starPercents(detail, productId).reduce((sum, val) => sum + (parseInt(val, 10) || 0) / 100, 0);
}
